import Vue from 'vue'
import VueRouter, { NavigationGuard, RouteConfig, Route } from 'vue-router'
import LoginPage from '@/views/LoginPage.vue'
import ErrorPage from '@/views/ErrorPage.vue'
import ProjectIndex from '@/views/ProjectIndex.vue'
import TestQueue from '@/views/TestQueue.vue'
import ResponsesByQuestions from '@/components/Survey/ResponseManagement/ResponsesByQuestions.vue'
import ResponsesByRespondents from '@/components/Survey/ResponseManagement/ResponsesByRespondents.vue'
import SharedReports from '@/views/SharedReports.vue'
import SharedResults from '@/views/SharedResults.vue'
import * as db from '@shared/types/database'
import store from '@/store'
import { getAuthToken, removeAuthToken } from '@/utilities/auth-token'
import analytics from '@/analytics'
import { NODE_ENV } from '@/config'
import api from '@/api'
import LiveCharts from '@/components/Survey/LiveCharts.vue'

let currentReleaseVersion: string | undefined

export const sharedViewRouteName = 'Shared Results'

// This interferes with mocking vue router, so just disable it in testing
if (!process || NODE_ENV !== 'test') {
  Vue.use(VueRouter)
}

const authenticate: NavigationGuard = (to, from, next) => {
  if (getAuthToken()) {
    next()
  } else {
    const path = window.location.pathname + window.location.search + window.location.hash
    const login = { name: 'login', params: { continue: path } }
    next(login)
  }
}

const clearAuth: NavigationGuard = (to, from, next) => {
  removeAuthToken()
  next()
}

const routes: RouteConfig[] = [
  {
    path: '/',
    name: 'home',
    beforeEnter: authenticate,
    redirect: '/workspaces'
  },
  {
    path: '/workspaces',
    name: 'workspaces',
    meta: {
      title: () => {
        return 'Workspaces'
      }
    },
    beforeEnter: authenticate,
    component: () => import('@/views/WorkspacesIndex.vue')
  },
  {
    path: '/personal-settings',
    name: 'personalSettings',
    component: () => import('@/views/PersonalSettings.vue')
  },
  {
    path: '/:workspaceId/projects/:projectId/SharePreview',
    name: 'sharedReportPreviewProject',
    meta: { noNav: true },
    component: SharedReports
  },
  {
    path: '/:workspaceId/:testId/SharePreview/',
    name: 'sharedReportPreviewTest',
    meta: { noNav: true },
    component: SharedReports
  },
  {
    path: '/report/:reportId',
    name: 'viewSharedReport',
    meta: { noNav: true },
    component: SharedReports
  },
  {
    path: '/results/:sharedLinkHash',
    name: sharedViewRouteName,
    meta: { noNav: true },
    component: SharedResults
  },
  {
    path: '/organizations/:orgId/queue',
    name: 'organizationQueue',
    meta: {
      title: () => {
        return 'Queue'
      }
    },
    component: TestQueue,
    props: { orgMode: true }
  },
  {
    path: '/audiences/:workspaceId',
    beforeEnter: authenticate,
    name: 'audiences',
    meta: {
      title: () => {
        return 'Audiences'
      },
      showAudiences: true
    },
    component: () => import('@/views/AudienceManager.vue')
  },
  {
    path: '/:workspaceId/report/:reportId/edit',
    name: 'editSharedReport',
    meta: { noNav: true },
    component: SharedReports
  },
  {
    path: '/resources',
    name: 'resources',
    component: () => import('@/views/Resources.vue')
  },
  {
    path: '/workspaces/:workspaceId',
    beforeEnter: authenticate,
    component: () => import('@/views/Workspace.vue'),
    children: [
      {
        path: '',
        name: 'workspaceRoot',
        redirect: { name: 'projects' }
      },
      {
        path: 'personal-settings',
        name: 'personalSettingsInWorkspace',
        component: () => import('@/views/PersonalSettings.vue')
      },
      {
        path: 'projects',
        name: 'projects',
        component: ProjectIndex,
        meta: {
          title () {
            return `${store.state.workspace.workspace.name}`
          },
          showAudiences: true
        }
      },
      {
        path: 'queue',
        name: 'queue',
        component: TestQueue,
        props: { orgMode: false },
        meta: {
          title () {
            return `Queue - ${store.state.workspace.workspace.name}`
          },
          showAudiences: true
        }
      },
      {
        path: 'tests',
        name: 'tests',
        component: () => import('@/views/TestIndex.vue'),
        children: [],
        meta: {
          title () {
            return `Tests - ${store.state.workspace.workspace.name}`
          },
          showAudiences: true
        }
      },
      {
        path: 'reports',
        name: 'reports',
        component: ErrorPage,
        props: { message: 'Where reports will go' },
        meta: {
          title () {
            return `Reports - ${store.state.workspace.workspace.name}`
          },
          showAudiences: true
        }
      }
    ]
  },
  {
    name: 'project',
    path: '/workspaces/:workspaceId/projects/:projectId',
    beforeEnter: authenticate,
    component: () => import('@/views/ProjectDetails.vue'),
    meta: { showAudiences: true }
  },
  {
    path: '/workspaces/:workspaceId/projects/:projectId?/tests/:testId',
    alias: '/workspaces/:workspaceId/tests/:testId',
    beforeEnter: authenticate,
    component: () => import('@/views/TestDetails.vue'),
    meta: { showAudiences: true },
    children: [
      {
        path: 'livecharts*',
        redirect: { name: 'test', query: { admin: 'true' } }
      },
      {
        path: '',
        name: 'test',
        component: LiveCharts,
        props: { showManageResponses: false },
        meta: {
          title () {
            return `Charts - ${store.state.workspace.workspace.name}`
          },
          showAudiences: true
        }
      },
      {
        path: 'manage-responses',
        component: LiveCharts,
        props: { showManageResponses: true },
        meta: {
          title () {
            return `Manage Responses - ${store.state.workspace.workspace.name}`
          },
          showAudiences: true,
          preventDefault: true
        },
        children: [
          {
            path: '',
            name: 'respondents',
            redirect: { name: 'by-question' }
          },
          {
            path: 'by-question',
            name: 'by-question',
            meta: {
              title () {
                return `Manage Responses - ${store.state.workspace.workspace.name}`
              }
            },
            component: ResponsesByQuestions
          },
          {
            path: 'by-participant',
            name: 'by-participant',
            meta: {
              title () {
                return `Manage Responses - ${store.state.workspace.workspace.name}`
              }
            },
            component: ResponsesByRespondents
          }
        ]
      }
    ]
  },
  {
    name: 'workspaceSettings',
    path: '/workspaces/:workspaceId/settings',
    meta: {
      title: () => {
        return `Settings - ${store.state.workspace.workspace.name}`
      },
      showAudiences: true
    },
    beforeEnter: authenticate,
    component: () => import('@/views/WorkspaceSettings.vue')
  },
  {
    path: '/login',
    name: 'login',
    beforeEnter: clearAuth,
    component: LoginPage,
    meta: { noNav: true }
  },
  {
    path: '*',
    name: 'error-page',
    beforeEnter: authenticate,
    component: ErrorPage
  }
]

const router = new VueRouter({
  mode: 'history',
  base: '/',
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) return savedPosition // allows for history navigation with expected behavior of saving scroll location
    if (preventDefaultRouteBehavior(to, from)) return
    return { x: 0, y: 0 }
  }
})

function trackRoute (route: Route) {
  if (!route.name) {
    return
  }

  analytics.page(route.name, undefined, {
    ...route.params,
    path: route.path
  })
}

const ensureLatestReleaseVersion: NavigationGuard = async (to, from, next) => {
  const previousReleaseVersion = currentReleaseVersion
  currentReleaseVersion = await api.currentReleaseVersion()
  // If there is a new version out, reload the page grabbing the new assets
  if (previousReleaseVersion && previousReleaseVersion !== currentReleaseVersion) {
    window.location.href = to.fullPath
  }
}

const updateFeatureConfigAndWorkspace = async (workspaceId: string) => {
  if (store.state.feature.config.length === 0 || store.state.workspace.workspace.id !== workspaceId) {
    await Promise.all([
      store.dispatch.feature.getFeatureConfig({
        workspaceId: workspaceId,
        accountId: store.state.accountId,
        orgId: (store.state.workspace.workspace.payload && store.state.workspace.workspace.payload.organization.id) || null
      }),
      store.dispatch.updateWorkspace(workspaceId)
    ])
  }
}

const updateUserAcl = () => {
  if ((!store.state.user.acl || store.state.user.acl.id !== store.state.accountId) && store.state.accountId) {
    store.dispatch.user.fetchAcl(store.state.accountId)
  }
}

/**
 * Checks if navigating between routes with the meta data `preventDefault`
 */
const preventDefaultRouteBehavior = (to: Route, from: Route): boolean => {
  return to.matched.some(route => route.meta.preventDefault) &&
    from.matched.some(route => route.meta.preventDefault)
}

router.beforeEach(async (to, from, next) => {
  if (from.query.admin === 'true') {
    to.query.admin = 'true'
  } // retain admin query param between route changes

  if (preventDefaultRouteBehavior(to, from)) return next()

  trackRoute(to)

  ensureLatestReleaseVersion(to, from, next)

  const { authenticatedUser } = store.state.user
  if (authenticatedUser && !to.meta.noNav) {
    const workspaceId = to.params.workspaceId
    if (workspaceId) {
      await updateFeatureConfigAndWorkspace(workspaceId)
    }
    updateUserAcl()
  }
  next()
})

router.afterEach((to, from) => {
  if (to.name === 'test') {
    // We need the url params to grab the correct test from the store
    const test:db.Test[] | undefined = store.state.workspace.workspace.drops?.filter(drop => drop.id === parseInt(to.params.testId))
    if (test) {
      document.title = `${test[0].payload.title} - Feedback Loop`
    }
  } else {
    document.title = to.meta.title ? to.meta.title() + ' - Feedback Loop' : 'Feedback Loop'
  }
})

export default router
